import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle, Shield, Award, Clock, Users, FileCheck, Loader2, BadgeCheck, Building2, UserCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// IASME Certification Fee - required for all packages
const IASME_FEE = {
  priceId: "price_1SrQ1PDA6hdLblAPGwWFCdLo",
  amount: 384,
  label: "IASME Certification Fee",
};

const PACKAGES = [
  {
    id: "submission-only",
    name: "Submission Only",
    priceId: "price_1SrOOSDA6hdLblAPhZp4mTDY",
    price: 495,
    label: "Low touch",
    bestFor: "You've done the work, and want a quality check",
    includes: [
      "Review Cyber Essentials answers before submission",
      "Align wording to meet scheme expectations",
      "Highlight missing evidence and required fixes",
    ],
  },
  {
    id: "micro-essentials",
    name: "Micro Essentials",
    priceId: "price_1SrOOUDA6hdLblAPf7Dpv0HC",
    price: 995,
    label: "Most popular",
    bestFor: "You want it handled properly end-to-end",
    includes: [
      "Baseline review against latest requirements",
      "Gap analysis + prioritised fix plan",
      "Evidence and policy support",
      "Submission support",
    ],
    popular: true,
  },
  {
    id: "micro-essentials-plus",
    name: "Micro Essentials Plus",
    priceId: "price_1SrOOWDA6hdLblAPaJFGRCPe",
    price: 1495,
    label: "White-glove",
    bestFor: "You want faster delivery and hands-on remediation support",
    includes: [
      "Everything in Micro Essentials",
      "Extra remediation support hours",
      "Microsoft 365 or Google Workspace configuration guidance",
      "Optional 30-minute staff briefing",
    ],
  },
];

const PERMISSION_TEXT = "I confirm I am authorised to request Cyber Essentials support for this organisation, and the systems in scope are owned by us, or we have written permission to assess them.";

const CyberEssentialsMicroPage = () => {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState<typeof PACKAGES[0] | null>(null);
  const [permissionConfirmed, setPermissionConfirmed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleBuyNow = (pkg: typeof PACKAGES[0]) => {
    setSelectedPackage(pkg);
    setPermissionConfirmed(false);
    setDialogOpen(true);
  };

  const handleCheckout = async () => {
    if (!selectedPackage || !permissionConfirmed) return;

    setIsProcessing(true);
    try {
      const totalAmount = (selectedPackage.price + IASME_FEE.amount) * 100; // Total in pence
      const { data, error } = await supabase.functions.invoke("create-ce-checkout-session", {
        body: {
          packageId: selectedPackage.id,
          packageName: selectedPackage.name,
          priceId: selectedPackage.priceId,
          iasmePriceId: IASME_FEE.priceId,
          amountGbp: totalAmount,
          permissionConfirmed: true,
          permissionStatementText: PERMISSION_TEXT,
        },
      });

      if (error) throw error;
      if (data?.sessionUrl) {
        window.location.href = data.sessionUrl;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Cyber Essentials for Micro Businesses | 4FOX Security"
        description="Get Cyber Essentials certified with a simple fixed-price package for UK micro businesses. Win contracts, speed up due diligence, and reduce cyber risk."
        canonical="https://4fox.uk/services/cyber-essentials-micro"
      />
      <Navbar />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-background to-accent/5 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Cyber Essentials Package
                <span className="block text-2xl md:text-3xl font-normal text-muted-foreground mt-2">
                  Micro Businesses: 1–9 employees
                </span>
              </h1>
              <p className="text-xl text-accent font-semibold mb-4">
                Win more work. Reduce risk. Prove your basics.
              </p>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Cyber Essentials is a UK Government-backed certification that shows you've got the essential cyber controls in place. It helps with tenders, supplier due diligence, and customer trust.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-accent hover:bg-accent/90" onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}>
                  Buy a package
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/contact')}>
                  Request a fixed quote
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Advantage */}
        <section className="py-16 bg-secondary/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Why Get Certified?</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { icon: Award, text: "Win contracts and meet tender requirements" },
                  { icon: Clock, text: "Pass supplier due diligence faster" },
                  { icon: Shield, text: "Reduce common cyber risks" },
                  { icon: Users, text: "Build customer trust quickly" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 bg-card p-4 rounded-lg border">
                    <item.icon className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-foreground font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">What's Included</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  "Baseline review against the latest Cyber Essentials requirements",
                  "Gap analysis + fix plan (clear and prioritised)",
                  "Evidence and policy support (no bloated paperwork)",
                  "Submission completed with you, or for you",
                  "Guided support through to certification",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-accent/5 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-16 bg-secondary/20">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Choose Your Package</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {PACKAGES.map((pkg) => (
                  <Card key={pkg.id} className={`relative ${pkg.popular ? 'border-accent border-2 shadow-lg' : ''}`}>
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-semibold">
                          {pkg.label}
                        </span>
                      </div>
                    )}
                    {!pkg.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-muted text-muted-foreground px-3 py-1 rounded-full text-xs font-semibold">
                          {pkg.label}
                        </span>
                      </div>
                    )}
                    <CardHeader className="pt-8">
                      <CardTitle className="text-xl">{pkg.name}</CardTitle>
                      <div className="mt-2">
                        <span className="text-4xl font-bold">£{(pkg.price + IASME_FEE.amount).toLocaleString()}</span>
                        <span className="text-lg text-muted-foreground ml-1">total</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        £{pkg.price.toLocaleString()} service + £{IASME_FEE.amount} IASME fee
                      </p>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="mb-4 text-foreground/80">
                        Best for: {pkg.bestFor}
                      </CardDescription>
                      <ul className="space-y-2 mb-6">
                        {pkg.includes.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className={`w-full ${pkg.popular ? 'bg-accent hover:bg-accent/90' : ''}`}
                        variant={pkg.popular ? "default" : "outline"}
                        onClick={() => handleBuyNow(pkg)}
                      >
                        Buy now
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Disclaimers */}
              <div className="mt-8 text-xs text-muted-foreground space-y-1 text-center">
                <p>IASME certification fees are separate and billed directly by the certification body.</p>
                <p>Certification depends on meeting Cyber Essentials requirements.</p>
                <p>We provide guidance and support, but no service can guarantee certification.</p>
                <p>Only available to organisations with permission to assess the systems in scope.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">How It Works</h2>
              <div className="grid sm:grid-cols-4 gap-4">
                {[
                  { step: 1, title: "Buy package", icon: FileCheck },
                  { step: 2, title: "Complete onboarding form (5 min)", icon: Users },
                  { step: 3, title: "4FOX completes baseline + fix plan", icon: Shield },
                  { step: 4, title: "Submit and get certified", icon: Award },
                ].map((item) => (
                  <div key={item.step} className="text-center">
                    <div className="w-12 h-12 mx-auto bg-accent/10 rounded-full flex items-center justify-center mb-3">
                      <item.icon className="w-6 h-6 text-accent" />
                    </div>
                    <div className="text-sm font-medium">{item.title}</div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-6 text-center">
                <div className="bg-accent/10 px-6 py-3 rounded-lg">
                  <span className="font-semibold">Typical delivery:</span> 1–2 weeks
                </div>
                <div className="bg-accent/10 px-6 py-3 rounded-lg">
                  <span className="font-semibold">Your time:</span> 2–3 short sessions
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-secondary/20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-2">
                <AccordionItem value="time" className="bg-card rounded-lg border px-4">
                  <AccordionTrigger>How long does it take?</AccordionTrigger>
                  <AccordionContent>
                    Most micro businesses complete certification within 1–2 weeks. Your active involvement is typically 2–3 short working sessions.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="fail" className="bg-card rounded-lg border px-4">
                  <AccordionTrigger>Can we fail Cyber Essentials?</AccordionTrigger>
                  <AccordionContent>
                    Yes, if the required controls aren't in place at assessment time. Our packages include gap analysis and remediation support to maximise your chances of first-time success.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="tools" className="bg-card rounded-lg border px-4">
                  <AccordionTrigger>Will we need new tools?</AccordionTrigger>
                  <AccordionContent>
                    Usually not. Most micro businesses already have the basics in place—they just need proper configuration and documentation.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="plus" className="bg-card rounded-lg border px-4">
                  <AccordionTrigger>Is Cyber Essentials the same as Cyber Essentials Plus?</AccordionTrigger>
                  <AccordionContent>
                    No. Cyber Essentials is a self-assessment verified by a certification body. Cyber Essentials Plus includes hands-on technical testing. These packages are for Cyber Essentials (the standard certification).
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="systems" className="bg-card rounded-lg border px-4">
                  <AccordionTrigger>What systems do you support?</AccordionTrigger>
                  <AccordionContent>
                    Windows and macOS devices, Microsoft 365, Google Workspace, and common UK business tools. If you have unusual systems, contact us for a quote.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Trust */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div className="p-6">
                  <BadgeCheck className="w-10 h-10 mx-auto text-accent mb-3" />
                  <h3 className="font-semibold mb-1">UK Government-backed scheme</h3>
                  <p className="text-sm text-muted-foreground">Official NCSC certification</p>
                </div>
                <div className="p-6">
                  <Building2 className="w-10 h-10 mx-auto text-accent mb-3" />
                  <h3 className="font-semibold mb-1">Designed for micro businesses</h3>
                  <p className="text-sm text-muted-foreground">Packages built for 1–9 employees</p>
                </div>
                <div className="p-6">
                  <UserCheck className="w-10 h-10 mx-auto text-accent mb-3" />
                  <h3 className="font-semibold mb-1">Experienced Fractional CISO</h3>
                  <p className="text-sm text-muted-foreground">Delivered by security professionals</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to get certified?</h2>
            <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">
              Choose a package and get started today. Most businesses complete certification within 1–2 weeks.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}>
                Buy your package
              </Button>
              <Button size="lg" variant="outline" className="border-white/80 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={() => navigate('/contact')}>
                Request a fixed quote
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Permission Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Before Checkout</DialogTitle>
            <DialogDescription>
              You're about to purchase: <strong>{selectedPackage?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Price breakdown */}
            <div className="bg-secondary/50 p-4 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span>{selectedPackage?.name} service</span>
                <span>£{selectedPackage?.price.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>IASME Certification Fee</span>
                <span>£{IASME_FEE.amount}</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span>£{selectedPackage ? (selectedPackage.price + IASME_FEE.amount).toLocaleString() : 0}</span>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox 
                id="permission" 
                checked={permissionConfirmed}
                onCheckedChange={(checked) => setPermissionConfirmed(checked === true)}
              />
              <label htmlFor="permission" className="text-sm leading-relaxed cursor-pointer">
                {PERMISSION_TEXT}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCheckout} 
              disabled={!permissionConfirmed || isProcessing}
              className="bg-accent hover:bg-accent/90"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Continue to payment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default CyberEssentialsMicroPage;
